import React from "react";
import { motion } from "framer-motion";

const journeyData = [
  {
    phase: "TODAY",
    title: "AI & Business Automation",
    description:
      "Membantu bisnis bekerja lebih cepat dan mendapatkan lebih banyak customer.",
    image:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=85",
    accent: "cyan",
  },
  {
    phase: "NEXT",
    title: "Industrial Digitalization",
    description:
      "Membantu perusahaan menghubungkan aset, mesin, dan data.",
    image:
      "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1200&q=85",
    accent: "cyan",
  },
  {
    phase: "THEN",
    title: "Software Platform",
    description:
      "Mengubah solusi menjadi recurring SaaS.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=85",
    accent: "cyan",
  },
  {
    phase: "NEXT GENERATION",
    title: "Energy & Automation",
    description:
      "Membantu industri mengoptimalkan energi dan proses operasional.",
    image:
      "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1200&q=85",
    accent: "cyan",
  },
  {
    phase: "FUTURE",
    title: "Robotics & Autonomous Systems",
    description:
      "Membangun sistem yang tidak hanya memberikan informasi, tapi mampu bertindak secara otomatis.",
    image:
      "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=1200&q=85",
    accent: "orange",
  },
];

const Journey = () => {
  return (
    <section className="relative overflow-hidden bg-[#090d12] px-6 py-24 text-white sm:px-10 lg:px-[14.7%]">
      
      {/* Background glow */}
      <div className="pointer-events-none absolute left-[20%] top-[10%] h-[500px] w-[500px] rounded-full bg-cyan-500/[0.025] blur-[120px]" />

      <div className="relative mx-auto max-w-[1100px]">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
          className="mb-20"
        >
          <div className="mb-7 font-mono text-[11px] tracking-[0.18em] text-[#8fa2bd]">
            09 — PERJALANAN
          </div>

          <h2 className="text-[38px] font-semibold tracking-[-0.025em] text-gray-100 sm:text-[44px] lg:text-[48px]">
            Today, Next, dan Future.
          </h2>
        </motion.div>

        {/* TIMELINE */}
        <div className="relative">

          {/* Vertical line */}
          <div className="absolute left-[7px] top-2 bottom-4 w-px bg-[#27303b] sm:left-[7px]" />

          {/* Animated line */}
          <motion.div
            initial={{ height: 0 }}
            whileInView={{ height: "100%" }}
            viewport={{ once: true }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
            className="absolute left-[7px] top-2 w-px origin-top bg-gradient-to-b from-[#00e5d4] via-[#00e5d4] to-[#ffae00]"
          />

          <div className="space-y-14">
            {journeyData.map((item, index) => (
              <JourneyItem
                key={item.phase}
                item={item}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const JourneyItem = ({ item, index }) => {
  const isFuture = item.accent === "orange";

  return (
    <motion.div
      initial={{
        opacity: 0,
        x: 50,
      }}
      whileInView={{
        opacity: 1,
        x: 0,
      }}
      viewport={{
        once: true,
        amount: 0.25,
      }}
      transition={{
        duration: 0.7,
        delay: index * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="relative pl-8 sm:pl-12"
    >

      {/* Timeline dot */}
      <motion.div
        initial={{
          scale: 0,
          opacity: 0,
        }}
        whileInView={{
          scale: 1,
          opacity: 1,
        }}
        viewport={{
          once: true,
          amount: 0.5,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 15,
          delay: index * 0.1,
        }}
        className={`absolute left-0 top-1 z-10 h-[14px] w-[14px] -translate-x-1/2 rounded-full border-2 bg-[#090d12] ${
          isFuture
            ? "border-[#ffae00]"
            : "border-[#00e5d4]"
        }`}
      />

      <div className="grid gap-7 lg:grid-cols-[1fr_370px] lg:items-start">

        {/* CONTENT */}
        <div>
          {/* Phase */}
          <motion.div
            initial={{ opacity: 0, letterSpacing: "0em" }}
            whileInView={{
              opacity: 1,
              letterSpacing: "0.12em",
            }}
            viewport={{
              once: true,
              amount: 0.5,
            }}
            transition={{
              duration: 0.6,
              delay: 0.15,
            }}
            className={`mb-3 font-mono text-[11px] ${
              isFuture
                ? "text-[#ffae00]"
                : "text-[#8fa2bd]"
            }`}
          >
            {item.phase}
          </motion.div>

          {/* Title */}
          <h3 className="mb-2 text-[18px] font-semibold leading-tight text-gray-100 sm:text-[19px]">
            {item.title}
          </h3>

          {/* Description */}
          <p className="max-w-[650px] text-[14px] leading-7 text-[#8fa2bd]">
            {item.description}
          </p>
        </div>

        {/* IMAGE */}
        <motion.div
          initial={{
            opacity: 0,
            scale: 0.9,
            x: 30,
          }}
          whileInView={{
            opacity: 1,
            scale: 1,
            x: 0,
          }}
          viewport={{
            once: true,
            amount: 0.25,
          }}
          transition={{
            duration: 0.8,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1],
          }}
          whileHover={{
            scale: 1.025,
          }}
          className="group relative overflow-hidden border border-[#27303b] bg-[#111720]"
        >

          {/* Image */}
          <motion.img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="h-[190px] w-full object-cover opacity-70 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-100 group-hover:grayscale-0"
          />

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#090d12] via-transparent to-transparent opacity-70" />

          {/* Accent */}
          <div
            className={`absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100 ${
              isFuture
                ? "bg-[#ffae00]"
                : "bg-[#00e5d4]"
            }`}
          />

          {/* Image label */}
          <div className="absolute bottom-3 left-4 font-mono text-[9px] tracking-[0.15em] text-white/70">
            {String(index + 1).padStart(2, "0")} / {item.phase}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Journey;